import React from 'react';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../form-kit';
import { SegmentedControl } from '../segmented-control/segmented-control';
import { ChipGroup, ToggleChip } from '../toggle-chip/toggle-chip';
import { WeekdayPicker } from '../weekday-picker/weekday-picker';
import type { PassLimits, PassScheduleKind } from '../../interfaces/visitor-pass';
import { addDays, formatPassDate, isRealDate } from '../../utils/pass-schedule';
import {
  Disclosure,
  DisclosureBody,
  Fieldset,
  Legend,
  Required,
} from './pass-editor.styled';

/**
 * WHEN — the schedule half of the pass editor (RP-2.9).
 *
 * One time: a date, and either "Any time" (the whole desk day) or a window.
 * Regular: the start date, the weekdays, how often, the window, and an end
 * date — required, because an open-ended standing pass is exactly what a
 * building should not accumulate (the server refuses a regular pass without
 * one).
 *
 * Every control carries a `name` (inputs) or a `data-field` (groups) equal to
 * the server's field key, so a 400 naming `valid_until` or `days` lands on the
 * control that caused it. The date inputs take their min/max from the server's
 * `limits`, never a second copy of them.
 */

export interface ScheduleFieldsValue {
  schedule_kind: PassScheduleKind;
  valid_from: string;
  valid_until: string;
  days: number[];
  interval_weeks: number;
  any_time: boolean;
  window_start: string;
  window_end: string;
}

export type FieldErrors = Partial<Record<string, string>>;

const KIND_OPTIONS: ReadonlyArray<{ value: PassScheduleKind; label: string }> = [
  { value: 'once', label: 'One time' },
  { value: 'weekly', label: 'Regular' },
];

/* A regular pass that has started: "One time" is not offered (the server
   refuses it — a started pass switched to one time would end on a day already
   gone, migration 129). */
const KIND_OPTIONS_STARTED: ReadonlyArray<{
  value: PassScheduleKind;
  label: string;
  disabled?: boolean;
}> = [
  { value: 'once', label: 'One time', disabled: true },
  { value: 'weekly', label: 'Regular' },
];

export const ONCE_LOCKED_HINT =
  'This pass has already started. End it and add a new one for a single visit.';

/* BEFORE 07:00 THE DESK IS STILL ON YESTERDAY'S LIST. The desk day runs 07:00
   to 07:00, and an "any time" visit is the desk day of its date — so a visit
   added at half past midnight for the calendar "today" is not expected until
   07:00. The form says so, and the front desk's own form offers "Tonight". */
export const TONIGHT_HINT =
  'It is before 07:00. Tonight is the list the front desk is using now; Today starts at 07:00.';
export const beforeSevenHint = (deskDate: string): string =>
  `It is before 07:00, so the front desk expects this visitor from 07:00. For a visitor arriving now, choose ${deskDate}.`;

/** Said under Starts on when "every 2–4 weeks" moved it to the first visit. */
export const startMovedHint = (weeks: number): string =>
  `Moved to the first visit, so every ${weeks} weeks is counted from this date.`;

const REPEAT_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: 'Every week' },
  { value: 2, label: 'Every 2 weeks' },
  { value: 3, label: 'Every 3 weeks' },
  { value: 4, label: 'Every 4 weeks' },
];

/** "a year" for 365/366, otherwise the day count. */
const spanWords = (days: number): string =>
  days >= 365 && days <= 366 ? 'a year' : `${days} days`;

export interface PassScheduleFieldsProps {
  value: ScheduleFieldsValue;
  onChange: (patch: Partial<ScheduleFieldsValue>) => void;
  errors: FieldErrors;
  limits: PassLimits;
  /** The building's calendar date. */
  today: string;
  /** The earliest day a new pass may start: the building's desk date — yesterday
   *  before 07:00 (migration 129). Left out = today. */
  fromFloor?: string;
  /** A pass that has started cannot move its start date (the server's rule). */
  startLocked: boolean;
  /** A REGULAR pass that has started cannot become a one-time visit: the
   *  "One time" choice is shown disabled, with the reason under it. */
  onceLocked?: boolean;
  /** The editor moved Starts on to the first visiting day because the pass
   *  repeats every 2–4 weeks (owner decision RP-D22). Said under the field. */
  startMoved?: boolean;
  /** True while Ends on still holds the date the editor filled in. */
  untilDefaulted: boolean;
  /** Prefix for element ids, unique per editor. */
  idPrefix: string;
  disabled?: boolean;
  /**
   * The front desk at a "one-time visits only" building (migration 118): no
   * One time / Regular choice, and the date is Today or Tomorrow — the only
   * days the server accepts from it.
   */
  onceOnly?: boolean;
}

export const PassScheduleFields: React.FC<PassScheduleFieldsProps> = ({
  value,
  onChange,
  errors,
  limits,
  today,
  fromFloor,
  startLocked,
  onceLocked = false,
  startMoved = false,
  untilDefaulted,
  idPrefix,
  disabled = false,
  onceOnly = false,
}) => {
  const id = (key: string) => `${idPrefix}-${key}`;
  const errorId = (key: string) => `${idPrefix}-${key}-error`;
  const describedBy = (key: string, ...more: Array<string | false | undefined>) =>
    [errors[key] ? errorId(key) : '', ...more].filter(Boolean).join(' ') || undefined;

  const weekly = value.schedule_kind === 'weekly';
  // Only while "One time" is not the choice already made.
  const lockOnce = onceLocked && weekly;
  const movedNote = startMoved && weekly && value.interval_weeks > 1 && !startLocked;
  const fromMax = addDays(
    today,
    weekly ? limits.weekly_max_days : limits.once_max_ahead_days
  );
  const untilFloor =
    isRealDate(value.valid_from) && value.valid_from > today ? value.valid_from : today;
  const untilMax = addDays(
    isRealDate(value.valid_from) ? value.valid_from : today,
    limits.weekly_max_days
  );
  const overnight =
    !value.any_time &&
    !!value.window_start &&
    !!value.window_end &&
    value.window_end <= value.window_start;

  const tomorrow = addDays(today, 1);
  // The desk day: yesterday's date before 07:00 (fromFloor), today's after.
  const deskDay = fromFloor && fromFloor < today ? fromFloor : today;
  const beforeSeven = deskDay < today;
  /* "Tonight" is offered for an any-time visit only: with a window the clock
     times already say when, on today's own date. */
  const offerTonight = beforeSeven && value.any_time;
  const onceDays: ReadonlyArray<{ value: string; label: string }> = [
    ...(offerTonight ? [{ value: deskDay, label: 'Tonight' }] : []),
    { value: today, label: 'Today' },
    { value: tomorrow, label: 'Tomorrow' },
  ];
  const onceDay =
    value.valid_from === tomorrow
      ? tomorrow
      : offerTonight && value.valid_from === deskDay
        ? deskDay
        : today;

  return (
    <>
      {onceOnly ? (
        <Fieldset data-field="valid_from" data-invalid={errors.valid_from ? 'true' : undefined}>
          <Legend>Day</Legend>
          <SegmentedControl
            label="Day of the visit"
            options={onceDays}
            value={onceDay}
            onChange={next => onChange({ schedule_kind: 'once', valid_from: next })}
            disabled={disabled}
          />
          <FieldHint id={id('once-hint')}>
            {offerTonight
              ? TONIGHT_HINT
              : 'The front desk can add one-time visits for today or tomorrow.'}
          </FieldHint>
          <FieldError id={errorId('valid_from')} message={errors.valid_from || errors.schedule_kind} />
        </Fieldset>
      ) : (
      <>
      <Fieldset data-field="schedule_kind" data-invalid={errors.schedule_kind ? 'true' : undefined}>
        <Legend>When</Legend>
        <SegmentedControl
          label="How often this visitor comes"
          options={lockOnce ? KIND_OPTIONS_STARTED : KIND_OPTIONS}
          value={value.schedule_kind}
          onChange={next => onChange({ schedule_kind: next })}
          disabled={disabled}
        />
        {lockOnce ? <FieldHint id={id('schedule_kind-hint')}>{ONCE_LOCKED_HINT}</FieldHint> : null}
        <FieldError id={errorId('schedule_kind')} message={errors.schedule_kind} />
      </Fieldset>

      <Field>
        <Label htmlFor={id('valid_from')}>
          {weekly ? 'Starts on' : 'Date'}
          <Required aria-hidden="true">*</Required>
        </Label>
        <Input
          id={id('valid_from')}
          name="valid_from"
          type="date"
          value={value.valid_from}
          min={startLocked ? undefined : fromFloor && fromFloor < today ? fromFloor : today}
          max={startLocked ? undefined : fromMax}
          readOnly={startLocked}
          aria-required="true"
          aria-invalid={errors.valid_from ? true : undefined}
          aria-describedby={describedBy(
            'valid_from',
            startLocked && id('valid_from-hint'),
            movedNote && id('valid_from-moved')
          )}
          hasError={!!errors.valid_from}
          disabled={disabled}
          onChange={event => onChange({ valid_from: event.target.value })}
        />
        {startLocked ? (
          <FieldHint id={id('valid_from-hint')}>
            This pass has already started, so its start date is fixed.
          </FieldHint>
        ) : null}
        {movedNote ? (
          <FieldHint id={id('valid_from-moved')}>{startMovedHint(value.interval_weeks)}</FieldHint>
        ) : null}
        {beforeSeven && !weekly && value.any_time && value.valid_from === today && !startLocked ? (
          <FieldHint id={id('valid_from-before-seven')}>
            {beforeSevenHint(formatPassDate(deskDay, { weekday: 'short', day: 'numeric', month: 'short' }))}
          </FieldHint>
        ) : null}
        <FieldError id={errorId('valid_from')} message={errors.valid_from} />
      </Field>
      </>
      )}

      <Disclosure open={weekly}>
        <DisclosureBody>
          <Fieldset data-field="days" data-invalid={errors.days ? 'true' : undefined}>
            <Legend>
              Days
              <Required aria-hidden="true">*</Required>
            </Legend>
            <WeekdayPicker
              label="Visiting days"
              value={value.days}
              onChange={days => onChange({ days })}
              disabled={disabled}
            />
            <FieldError id={errorId('days')} message={errors.days} />
          </Fieldset>

          <Field>
            <Label htmlFor={id('interval_weeks')}>Repeat</Label>
            <Select
              id={id('interval_weeks')}
              name="interval_weeks"
              value={String(value.interval_weeks)}
              aria-invalid={errors.interval_weeks ? true : undefined}
              aria-describedby={describedBy('interval_weeks')}
              disabled={disabled}
              onChange={event => onChange({ interval_weeks: Number(event.target.value) })}
            >
              {REPEAT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FieldError id={errorId('interval_weeks')} message={errors.interval_weeks} />
          </Field>
        </DisclosureBody>
      </Disclosure>

      <Fieldset>
        <Legend>Time</Legend>
        <ChipGroup aria-label="Time of the visit">
          <ToggleChip
            pressed={value.any_time}
            onToggle={on =>
              onChange({
                any_time: on,
                // "Tonight" is an any-time day: with a window, the visit is on
                // today's own date at those times.
                ...(onceOnly && !on && value.valid_from < today ? { valid_from: today } : {}),
              })
            }
            disabled={disabled}
          >
            Any time
          </ToggleChip>
        </ChipGroup>
        <FieldHint>
          {value.any_time
            ? 'Any time during the desk day, 07:00 to 07:00 the next morning.'
            : "Times are the building's local time."}
        </FieldHint>
      </Fieldset>

      <Disclosure open={!value.any_time}>
        <DisclosureBody>
          <FieldRow>
            <Field>
              <Label htmlFor={id('window_start')}>From</Label>
              <Input
                id={id('window_start')}
                name="window_start"
                type="time"
                value={value.window_start}
                aria-required="true"
                aria-invalid={errors.window_start ? true : undefined}
                aria-describedby={describedBy('window_start')}
                hasError={!!errors.window_start}
                disabled={disabled}
                onChange={event => onChange({ window_start: event.target.value })}
              />
              <FieldError id={errorId('window_start')} message={errors.window_start} />
            </Field>
            <Field>
              <Label htmlFor={id('window_end')}>To</Label>
              <Input
                id={id('window_end')}
                name="window_end"
                type="time"
                value={value.window_end}
                aria-required="true"
                aria-invalid={errors.window_end ? true : undefined}
                aria-describedby={describedBy('window_end', overnight && id('window_end-hint'))}
                hasError={!!errors.window_end}
                disabled={disabled}
                onChange={event => onChange({ window_end: event.target.value })}
              />
              {overnight ? (
                <FieldHint id={id('window_end-hint')}>Ends the next day.</FieldHint>
              ) : null}
              <FieldError id={errorId('window_end')} message={errors.window_end} />
            </Field>
          </FieldRow>
        </DisclosureBody>
      </Disclosure>

      <Disclosure open={weekly}>
        <DisclosureBody>
          <Field>
            <Label htmlFor={id('valid_until')}>
              Ends on
              <Required aria-hidden="true">*</Required>
            </Label>
            <Input
              id={id('valid_until')}
              name="valid_until"
              type="date"
              value={value.valid_until}
              min={untilFloor}
              max={untilMax}
              aria-required="true"
              aria-invalid={errors.valid_until ? true : undefined}
              aria-describedby={describedBy('valid_until', id('valid_until-hint'))}
              hasError={!!errors.valid_until}
              disabled={disabled}
              onChange={event => onChange({ valid_until: event.target.value })}
            />
            <FieldHint id={id('valid_until-hint')}>
              {untilDefaulted
                ? `Filled in as three months from today. A regular pass can run for up to ${spanWords(
                    limits.weekly_max_days
                  )}.`
                : `A regular pass can run for up to ${spanWords(limits.weekly_max_days)}.`}
            </FieldHint>
            <FieldError id={errorId('valid_until')} message={errors.valid_until} />
          </Field>
        </DisclosureBody>
      </Disclosure>
    </>
  );
};
